import type { BannerSize } from '@/api/generated/schemas';

/**
 * Creative geometry — what a banner-shaped container is, and what it
 * does to an image put inside it. Shared by banners and banner
 * actions, so neither owns it.
 *
 * Constants are pinned from the mobile apps and duplicated here on
 * purpose: they must drift only when someone consciously re-pins them
 * against the sources named below.
 */

/** BannerSize.kt */
export const RATIO: Record<BannerSize, { w: number; h: number }> = {
  small: { w: 16, h: 10 },
  large: { w: 4, h: 5 },
};

/** NovBanner.kt:61 */
export const CORNER_RADIUS = 20;

export const ratioLabel = (size: BannerSize): string =>
  `${RATIO[size].w}:${RATIO[size].h}`;

/** Crop under this is rounding, not something to tell the editor about. */
export const CROP_TOLERANCE = 0.01;

export interface CropInfo {
  /** Share of the creative the container hides, 0…1. */
  loss: number;
  /** Aspect ratio of the stored creative. */
  sourceRatio: number;
}

/**
 * What `object-fit: cover` throws away: the creative is scaled to
 * cover the container, so the excess of the longer axis is cut. Null
 * when the stored dimensions are unknown — nothing to compute from.
 */
export const cropInfo = (
  creative: { image_width: number | null; image_height: number | null },
  size: BannerSize
): CropInfo | null => {
  const { image_width: w, image_height: h } = creative;
  if (w == null || h == null || w <= 0 || h <= 0) return null;

  const sourceRatio = w / h;
  const targetRatio = RATIO[size].w / RATIO[size].h;
  const fit =
    Math.min(sourceRatio, targetRatio) / Math.max(sourceRatio, targetRatio);
  return { loss: 1 - fit, sourceRatio };
};

/** Whether a crop is worth showing the editor. */
export const isLossy = (crop: CropInfo | null): boolean =>
  crop != null && crop.loss > CROP_TOLERANCE;
